import { useDraggable } from "@dnd-kit/core";
import {
  faBook,
  faFolder,
  faFolderMinus,
  faLink,
  faPen,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { twJoin } from "tailwind-merge";

import { startsWithInternalPrefix } from "src/api/constants";
import { Flow, FlowVersionStatus } from "src/api/flowTypes";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { ResourceCard } from "src/base-components/ResourceCard";
import {
  ResourceStatusPill,
  ResourceStatusPillContainer,
} from "src/base-components/ResourceStatusPill";
import { RelatedFlowsPill } from "src/flow/Pills";
import { UserTip } from "src/flowReview/ReviewStatusPills";
import { useFlowVersionReviews } from "src/flowReview/api/queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { DashboardPageParams } from "src/router/urls";
import { versionIsInReview } from "src/utils/flowVersion";
import { pluralize, sentanceList } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type FlowCardProps = {
  flow: Flow;
  onViewFlow: (flow: Flow) => void;
  onRename: (flow: Flow) => void;
  onCopy: (flow: Flow) => void;
  onDelete: (flow: Flow) => void;
  onViewApiDocs: (flow: Flow) => void;
  onMove?: (flow: Flow) => void;
  onRemoveFromFolder?: (flow: Flow) => void;
};

const ReviewPillBody: React.FC<{
  orgId: string;
  versionsInReviewIds: string[];
}> = ({ orgId, versionsInReviewIds }) => {
  const reviews = useFlowVersionReviews(versionsInReviewIds);
  const reviewers = reviews.flatMap((r) => {
    return Object.values(r.data?.reviewers_status || {});
  });
  const uniqueReviewerIds = [...new Set(reviewers.map((r) => r.reviewer_id))];
  const uniqueReviewers = uniqueReviewerIds.map((id) => {
    return reviewers.find((r) => r.reviewer_id === id);
  });
  const allFetched = reviews.every((r) => r.isSuccess);
  const errored = reviews.some((r) => r.isError);
  if (!allFetched || errored) return null;
  return (
    <div className="max-w-60">
      Review requested from{" "}
      {sentanceList(
        [...uniqueReviewers].map((r) => (
          <>
            {r && (
              <span className="whitespace-nowrap">
                <UserTip
                  key={r.reviewer_id}
                  orgId={orgId}
                  userId={r.reviewer_id}
                />
              </span>
            )}
          </>
        )),
      )}
    </div>
  );
};

const VersionStatusPills: React.FC<{
  publishedCount: number;
  draftCount: number;
  versionsInReviewIds: string[];
  orgId: string;
}> = ({ publishedCount, draftCount, versionsInReviewIds, orgId }) => {
  const reviewCount = versionsInReviewIds.length;
  return (
    <div className="flex items-center overflow-hidden">
      <ResourceStatusPillContainer>
        {publishedCount > 0 && (
          <ResourceStatusPill
            color="green"
            tooltip={{
              title: pluralize(publishedCount, "published version"),
            }}
            value={String(publishedCount)}
          />
        )}
        {reviewCount > 0 && (
          <ResourceStatusPill
            color="indigo"
            tooltip={{
              title: pluralize(reviewCount, "version") + " in review",
              body: (
                <ReviewPillBody
                  orgId={orgId}
                  versionsInReviewIds={versionsInReviewIds}
                />
              ),
            }}
            value={String(reviewCount)}
          />
        )}
        {draftCount > 0 && (
          <ResourceStatusPill
            color="yellow"
            tooltip={{
              title: pluralize(draftCount, "draft version"),
            }}
            value={String(draftCount)}
          />
        )}
      </ResourceStatusPillContainer>
    </div>
  );
};

export const FlowCardV2: React.FC<FlowCardProps> = ({
  flow,
  onViewFlow,
  onRename,
  onCopy,
  onDelete,
  onViewApiDocs,
  onMove,
  onRemoveFromFolder,
}) => {
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: flow.id,
  });
  const { flows } = useCapabilities();
  const isInternal = startsWithInternalPrefix(flow.slug);

  const publishedCount = flow.versions.filter(
    (v) => v.status === FlowVersionStatus.PUBLISHED,
  ).length;

  const draftCount = flow.versions.filter(
    (v) => v.status === FlowVersionStatus.DRAFT && !versionIsInReview(v),
  ).length;

  const versionsInReviewIds = flow.versions
    .filter((v) => versionIsInReview(v))
    .map((v) => v.id);

  const dropDownOptions: OptionsDropdownElement[] = (() => {
    const options: OptionsDropdownElement[] = flows.canEdit
      ? [{ key: "Edit details", icon: faPen, action: () => onRename(flow) }]
      : [];
    options.push({
      key: "Copy link",
      icon: faLink,
      action: () => onCopy(flow),
    });

    if (flows.canEdit) {
      if (onMove) {
        options.push({
          key: "Move to another folder",
          icon: faFolder,
          action: () => onMove(flow),
        });
      }
      if (flow.flow_folder_id && onRemoveFromFolder) {
        options.push({
          key: "Remove from folder",
          icon: faFolderMinus,
          action: () => onRemoveFromFolder(flow),
        });
      }
    }

    options.push({
      key: "View API docs",
      icon: faBook,
      action: () => onViewApiDocs(flow),
    });
    if (
      (publishedCount === 0 && flows.canDeleteUnpublished) ||
      (publishedCount > 0 && flows.canDeletePublished)
    ) {
      options.push(MENU_DIVIDER, {
        key: "Delete",
        icon: faTrashCan,
        action: () => onDelete(flow),
      });
    }

    return options;
  })();

  return (
    <div
      ref={setNodeRef}
      data-loc={`${flow.name}-card`}
      onClick={() => onViewFlow(flow)}
      {...listeners}
      {...attributes}
    >
      <ResourceCard
        description={
          flow.meta?.description ? flow.meta.description : "No description yet"
        }
        title={
          <div className="flex justify-between" data-loc="flow-card-name">
            {flow.name}
            {isInternal && (
              <Pill size="sm" variant="red">
                <Pill.Text>Internal</Pill.Text>
              </Pill>
            )}
          </div>
        }
      >
        <div className="mt-auto flex items-center gap-x-3">
          <VersionStatusPills
            draftCount={draftCount}
            orgId={orgId}
            publishedCount={publishedCount}
            versionsInReviewIds={versionsInReviewIds}
          />
          {flow.child_flows &&
            flow.parent_flows &&
            (flow.child_flows.length > 0 || flow.parent_flows.length > 0) && (
              <div className="mt-[1px] flex gap-x-2">
                <RelatedFlowsPill
                  flows={
                    flow.child_flows.length > 0
                      ? flow.child_flows
                      : flow.parent_flows
                  }
                  isParent={flow.child_flows.length > 0}
                  countOnly
                />
              </div>
            )}
          <div
            className="ml-auto"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <EllipsisOptionsDropdown
              buttonClassName={twJoin(
                "text-gray-500",
                "opacity-0 group-hover/resource-card:opacity-100",
              )}
              dataLoc={`${flow.name}-menu`}
              elements={dropDownOptions}
              placement="bottom start"
            />
          </div>
        </div>
      </ResourceCard>
    </div>
  );
};
